/**
 * MER-674: pure logic for the Executive board page.
 *
 * The page is the automated form of the replica AJ approved on 2026-09-14:
 * the workbook's four blocks, the five-card scorecard, a trend for any line
 * item, and the full month-by-month table with the note column. Everything
 * that can be computed without React lives here so node --test covers it
 * directly (the nowCutoff.mjs / navSections.mjs precedent); Executive.tsx
 * renders what comes back and execKpi.d.mts types it.
 *
 * Input rows are public.exec_kpi rows as PostgREST returns them: one row per
 * (sheet, line_item, period). A blank workbook cell has no row. A spreadsheet
 * error cell has value null and its raw text in cell_text. Units and
 * decimals are declared per row and never sniffed from a name (spec s10,
 * fix 1); the chart, the table and the CSV all read the same declaration.
 */

import { SOURCE_LABELS } from "./execLineSource.mjs";

export const BLOCK_ORDER = Object.freeze([
  "Operational Data",
  "Financial Data",
  "Operational KPIs",
  "Financial KPIs",
]);

/** Tab label for the company sheet (is_rollup = true). */
export const ROLLUP_LABEL = "Global Ops";

/** Copy on the awaiting-data card when the tenant has no exec_kpi rows. */
export const AWAITING_WORKBOOK_COPY = "Awaiting the executive KPI workbook";

/**
 * Copy on the error card when the read fails for any reason other than a
 * missing table. The raw PostgREST message never reaches the client; it
 * goes to console.warn.
 */
export const WORKBOOK_UNAVAILABLE_COPY =
  "The executive workbook could not be loaded right now. Try again in a few minutes.";

/** Banner when the paged read hit its page cap with the last page still full. */
export function truncatedCopy(rowCount) {
  const n = Math.max(0, Math.trunc(Number(rowCount) || 0));
  return `Showing the first ${n.toLocaleString("en-US")} rows; the workbook is larger than expected`;
}

/** PostgREST's default page cap; the fetch loops in pages of this size. */
export const EXEC_KPI_PAGE_SIZE = 1000;

/** Trailing months summed into the TTM column. */
export const TTM_MONTHS = 12;

/**
 * The scorecard, in card order. Each role takes the first line item present
 * on the sheet from its name list, so a division sheet that reports lifts
 * rather than tons still fills the volume card. Each role carries its own
 * direction of goodness, so the card's colour never depends on the name:
 * volume, income, profit and margin up is good; opex up is bad.
 */
export const HERO_ROLES = Object.freeze([
  {
    role: "volume",
    domain: "operations",
    direction: "higher",
    names: ["Tons", "Total Lifts", "Total Hauls"],
  },
  { role: "income", domain: "financial", direction: "higher", names: ["Total Income"] },
  {
    role: "opex",
    domain: "financial",
    direction: "lower",
    names: ["Total OpEx (Disposal Included)"],
  },
  {
    role: "profit",
    domain: "financial",
    direction: "higher",
    names: ["Profit After OpEx", "Profit After Total OpEx (Disposal Included)"],
  },
  {
    role: "margin",
    domain: "financial",
    direction: "higher",
    names: ["Profitability After OpEx", "Profitability After Total OpEx (Disposal Included)"],
  },
]);

/* ------------------------------------------------------------------ */
/* Direction of goodness                                               */
/* ------------------------------------------------------------------ */

/**
 * Which way is good for a line item, from its name. An explicit
 * classification, not a substring rule: a profit line that mentions OpEx
 * ("Profit After Total OpEx (Disposal Included)") is higher-is-better; a
 * cost expressed against revenue ("Disposal % of Revenue") is
 * lower-is-better; a plain count ("Total Days Worked") is neutral and draws
 * no tone. The hero cards do not consult this at all: HERO_ROLES carries
 * an explicit direction per role.
 */
const HIGHER_IS_BETTER_RE = /profit|profitab|income|revenue|margin|lifts|hauls|tons|yards|utiliz/i;
const LOWER_IS_BETTER_RE =
  /cost|expense|cogs|opex|dry run|minutes|disposal (?:% of revenue|percent of revenue|cost)|fuel|labor|maintenance|miles per|miss/i;
/** The profit and income family: never a cost, whatever else the name says. */
const PROFIT_LIKE_RE = /profit|profitab|income|revenue|margin/i;
/** The cost family: a cost ratio is one of these that is not also profit-like. */
const COST_LIKE_RE = /cost|expense|cogs|opex/i;
/** "X % of Revenue" is a cost measured against revenue, unless X is profit. */
const COST_OF_REVENUE_RE = /(?:%|percent|pct)\s*of\s*revenue/i;
const PROFIT_WORD_RE = /profit|profitab|income|margin/i;

/** "higher" | "lower" | "neutral". */
export function goodnessDirection(lineItem) {
  const s = String(lineItem ?? "");
  const costOfRevenue = COST_OF_REVENUE_RE.test(s) && !PROFIT_WORD_RE.test(s);
  const profitLike = PROFIT_LIKE_RE.test(s) && !costOfRevenue;
  const costRatio = costOfRevenue || (COST_LIKE_RE.test(s) && !profitLike);
  if (HIGHER_IS_BETTER_RE.test(s) && !costRatio) return "higher";
  if (LOWER_IS_BETTER_RE.test(s) && !profitLike) return "lower";
  return "neutral";
}

export function higherIsBetter(lineItem) {
  return goodnessDirection(lineItem) === "higher";
}

export function lowerIsBetter(lineItem) {
  return goodnessDirection(lineItem) === "lower";
}

export const PROVENANCE_TAGS = Object.freeze({
  client_reported_gl: {
    tag: "GL",
    title: "Client-reported from the closed general ledger",
  },
  client_reported_operational: {
    tag: "Ops",
    title: "Client-reported operational count or measure (Navusoft, Samsara or a manual sheet)",
  },
  client_reported_assumption: {
    tag: "Assumed",
    title: "Client-reported assumption or placeholder, not a measurement",
  },
  client_reported_error: {
    tag: "Error",
    title: "The workbook cell holds a spreadsheet error such as #DIV/0!",
  },
  // S1-exec-from-imports: a line the page shows from Meridian's own figures.
  meridian_imports: {
    tag: "Imports",
    title: "Computed by Meridian from your imported data (Navusoft, Samsara, Ramp)",
  },
  // A line of the Meridian-derived board (public.exec_derived), for a tenant
  // with no workbook: every line is Meridian's, from the tenant's imports.
  meridian_derived: {
    tag: "Imports",
    title: "Computed by Meridian from your imported data",
  },
});

export const UNITS = Object.freeze(["usd", "usd_per_unit", "fraction", "count", "number"]);

/*
 * Confirmed with the data lane (MER-667 contract):
 *   - exec_kpi.decimals is the DISPLAY precision for the formatted unit, so a
 *     fraction row with decimals 1 renders 22.3% (not 0.2). Null falls back
 *     to the unit default below.
 *   - exec_kpi.division carries the display names "Jackson", "Hattiesburg"
 *     and "Dallas"; divisionLabel title-cases whatever arrives so a lower
 *     case or snake_case value reads the same.
 *   - exec_kpi.equipment_type carries the canonical codes "front_load",
 *     "roll_off" and "asl" (labelled Res). EQUIPMENT_LABELS keeps a fallback
 *     for an unknown code (title-cased, e.g. "Hook Lift") rather than
 *     dropping the sheet.
 */
const UNIT_DEFAULT_DECIMALS = { usd: 0, usd_per_unit: 2, fraction: 1, count: 0, number: 1 };

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ------------------------------------------------------------------ */
/* Small coercions                                                     */
/* ------------------------------------------------------------------ */

function toNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toInt(v) {
  const n = toNumber(v);
  return n === null ? null : Math.trunc(n);
}

/** One of UNITS, defaulting to "number" so an undeclared row still renders. */
export function normalizeUnit(unit) {
  const u = typeof unit === "string" ? unit.trim().toLowerCase() : "";
  return UNITS.includes(u) ? u : "number";
}

/** One of BLOCK_ORDER when the row's block matches, else the raw text. */
export function normalizeBlock(block) {
  const b = typeof block === "string" ? block.trim() : "";
  if (/^operational data/i.test(b)) return BLOCK_ORDER[0];
  if (/^financial data/i.test(b)) return BLOCK_ORDER[1];
  if (/^operational kpi/i.test(b)) return BLOCK_ORDER[2];
  if (/^financial kpi/i.test(b)) return BLOCK_ORDER[3];
  return b;
}

/* ------------------------------------------------------------------ */
/* Paging                                                              */
/* ------------------------------------------------------------------ */

/**
 * Inclusive PostgREST range for page `page` (0-based) of `size` rows, the
 * shape `.range(from, to)` takes. Mirrors the pipeline loader's
 * _fetch_pk_tuples paging (start = page * size, end = start + size - 1).
 */
export function pageBounds(page, size = EXEC_KPI_PAGE_SIZE) {
  const p = Math.max(0, Math.trunc(Number(page) || 0));
  const s = Math.max(1, Math.trunc(Number(size) || EXEC_KPI_PAGE_SIZE));
  return { from: p * s, to: p * s + s - 1 };
}

/* ------------------------------------------------------------------ */
/* Labels                                                              */
/* ------------------------------------------------------------------ */

/** "2026-07" -> "Jul-26". Anything else comes back unchanged. */
export function monthLabel(period) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period ?? ""));
  if (!m) return String(period ?? "");
  const idx = Number(m[2]) - 1;
  if (idx < 0 || idx > 11) return String(period);
  return `${MONTH_NAMES[idx]}-${m[1].slice(2)}`;
}

/**
 * "2026-09-11T14:02:33+00:00" -> "Sep 11, 2026". Parsed lexically from the
 * date part so a UTC offset cannot move the day (the format.ts precedent).
 */
export function formatReceived(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  if (!m) return null;
  const idx = Number(m[2]) - 1;
  if (idx < 0 || idx > 11) return null;
  return `${MONTH_NAMES[idx]} ${Number(m[3])}, ${m[1]}`;
}

const EQUIPMENT_LABELS = {
  front_load: "FEL",
  fel: "FEL",
  roll_off: "RO",
  ro: "RO",
  asl: "Res",
  side_load: "Res",
  rear_load: "Res",
  rl: "Res",
  residential: "Res",
  res: "Res",
};

const EQUIPMENT_RANK = { FEL: 0, RO: 1, Res: 2 };

function titleWord(word) {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/** "jackson" -> "Jackson"; "JXN" stays "JXN"; "central_coast" -> "Central Coast". */
export function divisionLabel(division) {
  return String(division ?? "")
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map(titleWord)
    .join(" ");
}

/** Canonical equipment_type -> the short LOB tag the board reads. */
export function equipmentLabel(equipmentType) {
  const key = String(equipmentType ?? "")
    .trim()
    .toLowerCase();
  if (!key) return "";
  return EQUIPMENT_LABELS[key] ?? divisionLabel(key);
}

/**
 * Tab label for a sheet: "Global Ops" for the rollup, else division plus
 * equipment ("Jackson FEL", "Hattiesburg Res"), falling back to the raw
 * sheet name when the row carries neither.
 */
export function sheetLabel(row) {
  if (!row) return "";
  // A derived-board row (public.exec_derived) names its own tab ("Main Roll
  // Off"). Workbook rows carry no sheet_label and read exactly as before.
  if (typeof row.sheet_label === "string" && row.sheet_label.trim() !== "") {
    return row.sheet_label.trim();
  }
  if (row.is_rollup === true) return ROLLUP_LABEL;
  const div = row.division ? divisionLabel(row.division) : "";
  const eq = row.equipment_type ? equipmentLabel(row.equipment_type) : "";
  if (div && eq) return `${div} ${eq}`;
  if (div) return div;
  return String(row.sheet ?? "");
}

/* ------------------------------------------------------------------ */
/* Grouping                                                            */
/* ------------------------------------------------------------------ */

/** The numeric value of an item for a month, or null (blank or error cell). */
export function itemValue(item, period) {
  const v = item && item.values ? item.values[period] : undefined;
  return typeof v === "number" ? v : null;
}

/** The raw text of an error cell for a month ("#DIV/0!"), or null. */
export function itemCellText(item, period) {
  const t = item && item.cellText ? item.cellText[period] : undefined;
  return typeof t === "string" ? t : null;
}

/** The latest month that carries a numeric value for the item, or null. */
export function itemLatestPeriod(item) {
  if (!item || !item.values) return null;
  const keys = Object.keys(item.values)
    .filter((k) => typeof item.values[k] === "number")
    .sort();
  return keys.length ? keys[keys.length - 1] : null;
}

/**
 * "Jan-26" when the item's note comes from a month older than its latest
 * populated month, so the table can say "(note from Jan-26)"; null when the
 * note is current or there is no note.
 */
export function noteFromLabel(item) {
  if (!item || typeof item.note !== "string" || typeof item.notePeriod !== "string") return null;
  const latest = itemLatestPeriod(item);
  return latest !== null && item.notePeriod < latest ? monthLabel(item.notePeriod) : null;
}

/** The small tag the table shows for a row's provenance. */
export function provenanceTag(provenance) {
  const key = typeof provenance === "string" ? provenance.trim().toLowerCase() : "";
  return PROVENANCE_TAGS[key] ?? { tag: "n/a", title: "No provenance declared on this row" };
}

function dominantProvenance(byPeriod, months) {
  const counts = new Map();
  let last = null;
  for (const m of months) {
    const p = byPeriod[m];
    if (!p) continue;
    counts.set(p, (counts.get(p) ?? 0) + 1);
    last = p;
  }
  if (counts.size === 0) return { provenance: null, mixed: false };
  let best = null;
  let bestCount = -1;
  for (const [p, c] of counts) {
    // Ties go to the latest month's provenance, which is what the reader is
    // looking at in the emphasised column.
    if (c > bestCount || (c === bestCount && p === last)) {
      best = p;
      bestCount = c;
    }
  }
  return { provenance: best, mixed: counts.size > 1 };
}

function compareItems(a, b) {
  const ao = a.lineOrder === null ? Number.POSITIVE_INFINITY : a.lineOrder;
  const bo = b.lineOrder === null ? Number.POSITIVE_INFINITY : b.lineOrder;
  if (ao !== bo) return ao - bo;
  return a.lineItem.localeCompare(b.lineItem);
}

/**
 * Group exec_kpi rows into sheets, blocks and items.
 *
 * Sheet order is workbook order as far as the rows can express it: the
 * rollup sheet first, then the division sheets by the customer's declared
 * division order (opts.divisionOrder, the manifest's `divisions` list,
 * matched case-insensitively), then by equipment (FEL, RO, Res), then by
 * sheet name. Items within a sheet follow line_order.
 *
 * @param {object[]} rows  exec_kpi rows.
 * @param {{ divisionOrder?: string[] }} [opts]
 */
export function groupRows(rows, opts = {}) {
  const divisionOrder = (opts.divisionOrder ?? []).map((d) => String(d).trim().toLowerCase());
  const monthSet = new Set();
  let workbookLatestPeriod = null;
  let receivedAt = null;
  const sheetMap = new Map();

  for (const row of rows ?? []) {
    if (!row || typeof row.sheet !== "string" || typeof row.line_item !== "string") continue;
    if (typeof row.period !== "string" || !/^\d{4}-\d{2}$/.test(row.period)) continue;
    const period = row.period;
    monthSet.add(period);
    if (
      typeof row.workbook_latest_period === "string" &&
      (workbookLatestPeriod === null || row.workbook_latest_period > workbookLatestPeriod)
    ) {
      workbookLatestPeriod = row.workbook_latest_period;
    }
    if (
      typeof row.created_date === "string" &&
      (receivedAt === null || row.created_date > receivedAt)
    ) {
      receivedAt = row.created_date;
    }

    let sheet = sheetMap.get(row.sheet);
    if (!sheet) {
      sheet = {
        sheet: row.sheet,
        label: sheetLabel(row),
        isRollup: row.is_rollup === true,
        division: typeof row.division === "string" ? row.division : null,
        equipmentType: typeof row.equipment_type === "string" ? row.equipment_type : null,
        // A derived-board row declares its tab order; workbook rows do not,
        // and sort as before.
        order: toInt(row.sheet_order),
        latest: null,
        itemMap: new Map(),
      };
      sheetMap.set(row.sheet, sheet);
    }
    let item = sheet.itemMap.get(row.line_item);
    if (!item) {
      item = {
        lineItem: row.line_item,
        block: normalizeBlock(row.block),
        lineOrder: toInt(row.line_order),
        unit: normalizeUnit(row.unit),
        decimals: toInt(row.decimals),
        additive: row.additive === true,
        provenance: null,
        provenanceMixed: false,
        note: null,
        notePeriod: null,
        values: {},
        cellText: {},
        provenanceByPeriod: {},
        noteByPeriod: {},
        // S1-exec-from-imports: rows that never met applyLineSources are the
        // workbook's, exactly as before the switch.
        source: row.source === "ours" ? "ours" : "workbook",
        sourceReason: typeof row.source_reason === "string" ? row.source_reason : null,
        workbookValues: {},
        workbookCellText: {},
        missingReason: {},
        // Derived board only (public.exec_derived): the scorecard card this
        // line fills, and whether the line holds no figure in any month
        // (awaitingReason, set below). Null on every workbook line.
        heroRole: typeof row.hero_role === "string" && row.hero_role ? row.hero_role : null,
        derived: row.provenance === "meridian_derived",
        awaitingReason: null,
        // Derived board, migration 0093: the page words a line with no figure
        // carries (the same on every month of a line).
        awaitingLabel:
          typeof row.awaiting_label === "string" && row.awaiting_label ? row.awaiting_label : null,
      };
      sheet.itemMap.set(row.line_item, item);
    }
    // One source per line: applyLineSources looks the source up once per
    // (sheet, line), so every row of a line carries the same one.
    if (item.source === "ours") {
      const wv = toNumber(row.workbook_value);
      if (wv !== null) item.workbookValues[period] = wv;
      else if (typeof row.workbook_cell_text === "string" && row.workbook_cell_text.trim() !== "") {
        item.workbookCellText[period] = row.workbook_cell_text.trim();
      }
      if (typeof row.missing_reason === "string" && row.missing_reason) {
        item.missingReason[period] = row.missing_reason;
      }
    }
    const v = toNumber(row.value);
    if (v !== null) {
      item.values[period] = v;
    } else if (typeof row.cell_text === "string" && row.cell_text.trim() !== "") {
      item.cellText[period] = row.cell_text.trim();
    }
    if (typeof row.provenance === "string" && row.provenance) {
      item.provenanceByPeriod[period] = row.provenance.trim().toLowerCase();
    }
    if (typeof row.note === "string" && row.note.trim() !== "") {
      item.noteByPeriod[period] = row.note.trim();
    }
    if (sheet.latest === null || period > sheet.latest) sheet.latest = period;
  }

  const months = [...monthSet].sort();

  const sheets = [...sheetMap.values()].map((s) => {
    const items = [...s.itemMap.values()].sort(compareItems);
    for (const item of items) {
      const { provenance, mixed } = dominantProvenance(item.provenanceByPeriod, months);
      item.provenance = provenance;
      item.provenanceMixed = mixed;
      // The note is the LATEST one the workbook carries for the row, and the
      // item remembers which month it came from so the table can say so
      // when that month is older than the latest value.
      const notePeriods = Object.keys(item.noteByPeriod).sort();
      item.notePeriod = notePeriods.length ? notePeriods[notePeriods.length - 1] : null;
      item.note = item.notePeriod === null ? null : item.noteByPeriod[item.notePeriod];
      if (item.derived && Object.keys(item.values).length === 0) {
        const reasons = Object.keys(item.missingReason).sort();
        item.awaitingReason = reasons.length
          ? item.missingReason[reasons[reasons.length - 1]]
          : "No figure from your imports";
      } else {
        item.awaitingLabel = null;
      }
    }
    const blocks = BLOCK_ORDER.map((block) => ({
      block,
      items: items.filter((it) => it.block === block),
    }));
    const eq = s.equipmentType ? equipmentLabel(s.equipmentType) : "";
    const divIdx = s.division ? divisionOrder.indexOf(s.division.trim().toLowerCase()) : -1;
    return {
      sheet: s.sheet,
      label: s.label,
      isRollup: s.isRollup,
      division: s.division,
      equipmentType: s.equipmentType,
      latest: s.latest,
      items,
      blocks,
      sortKey: [
        s.isRollup ? 0 : 1,
        s.order === null ? Number.POSITIVE_INFINITY : s.order,
        divIdx === -1 ? Number.POSITIVE_INFINITY : divIdx,
        EQUIPMENT_RANK[eq] ?? Number.POSITIVE_INFINITY,
      ],
    };
  });

  sheets.sort((a, b) => {
    for (let i = 0; i < a.sortKey.length; i += 1) {
      if (a.sortKey[i] !== b.sortKey[i]) return a.sortKey[i] - b.sortKey[i];
    }
    const ad = a.division ?? "";
    const bd = b.division ?? "";
    if (ad !== bd) return ad.localeCompare(bd);
    return a.sheet.localeCompare(b.sheet);
  });
  for (const s of sheets) delete s.sortKey;

  return {
    months,
    latest: months.length ? months[months.length - 1] : null,
    workbookLatestPeriod,
    receivedAt,
    sheets,
  };
}

/* ------------------------------------------------------------------ */
/* Scorecard                                                           */
/* ------------------------------------------------------------------ */

function findItem(sheet, name) {
  const want = String(name).trim().toLowerCase();
  return (sheet.items ?? []).find((it) => it.lineItem.trim().toLowerCase() === want) ?? null;
}

/**
 * The latest month that carries a value and the month before it, so a sheet
 * that ends early never shows a stale month as "latest" silently (spec s10,
 * fix 2).
 *
 *   direction  "up" | "down" | "flat" | null   the movement
 *   goodness   "higher" | "lower" | "neutral"  which way is good, from
 *              opts.direction (the hero roles) or goodnessDirection(name)
 *   tone       "good" | "bad" | "flat" | "neutral" | null
 *
 * pct is null against a zero prior month (no percent of nothing) and when
 * there is no prior populated month at all; `prev` tells those apart.
 */
export function momDelta(item, months, opts = {}) {
  if (!item || !Array.isArray(months)) return null;
  let i = months.length - 1;
  while (i >= 0 && itemValue(item, months[i]) === null) i -= 1;
  if (i < 0) return null;
  let j = i - 1;
  while (j >= 0 && itemValue(item, months[j]) === null) j -= 1;
  const cur = months[i];
  const curV = itemValue(item, cur);
  const prev = j >= 0 ? months[j] : null;
  const prevV = prev === null ? null : itemValue(item, prev);
  const goodness =
    opts.direction === "higher" || opts.direction === "lower" || opts.direction === "neutral"
      ? opts.direction
      : goodnessDirection(item.lineItem);
  let pct = null;
  let direction = null;
  let tone = null;
  if (prevV !== null && prevV !== 0) {
    pct = ((curV - prevV) / Math.abs(prevV)) * 100;
    const flat = Math.abs(pct) < 0.05;
    direction = flat ? "flat" : pct > 0 ? "up" : "down";
    if (flat) tone = "flat";
    else if (goodness === "neutral") tone = "neutral";
    else tone = (goodness === "higher" ? pct > 0 : pct < 0) ? "good" : "bad";
  }
  return { cur, curV, prev, prevV, pct, direction, goodness, tone };
}

/**
 * The five hero cards for a sheet. `item` is null when the sheet has none of
 * the role's line items. `stale` is true when the card's latest populated
 * month differs from `latest`, the workbook's own latest period (the page
 * passes workbookLatestPeriod ?? latest), NOT the sheet's own latest month:
 * a whole sheet that stopped a month early is stale on every card.
 */
export function heroCards(sheet, months, latest) {
  const ref =
    typeof latest === "string" && latest
      ? latest
      : Array.isArray(months) && months.length
        ? months[months.length - 1]
        : null;
  return HERO_ROLES.map((role) => {
    // A derived-board line names the card it fills (hero_role); a workbook
    // line never carries one, so the workbook keeps its name lookup.
    const byRole = sheet
      ? ((sheet.items ?? []).find((it) => it.heroRole === role.role) ?? null)
      : null;
    const item = sheet
      ? (byRole ?? role.names.map((n) => findItem(sheet, n)).find(Boolean) ?? null)
      : null;
    const delta = item ? momDelta(item, months, { direction: role.direction }) : null;
    return {
      role: role.role,
      domain: role.domain,
      item,
      delta,
      stale: Boolean(delta && ref && delta.cur !== ref),
    };
  });
}

/* ------------------------------------------------------------------ */
/* TTM                                                                 */
/* ------------------------------------------------------------------ */

/**
 * Trailing-twelve-month sum for an additive item over the last 12 entries of
 * `months`; null for a ratio, which cannot be summed (spec s10, fix 5). An
 * additive item with no value in the window returns value null, n 0.
 */
export function ttm(item, months) {
  if (!item || !item.additive) return null;
  const window = (months ?? []).slice(-TTM_MONTHS);
  let sum = 0;
  let n = 0;
  for (const m of window) {
    const v = itemValue(item, m);
    if (v !== null) {
      sum += v;
      n += 1;
    }
  }
  return {
    value: n ? sum : null,
    n,
    from: window.length ? window[0] : null,
    to: window.length ? window[window.length - 1] : null,
  };
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

function group(x, dp) {
  return x.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

/**
 * Format a value by its declared unit. `decimals` is the row's declared
 * display precision (percent points for a fraction); null falls back to the
 * unit default. Returns null for a non-number so callers render n/a.
 */
export function fmtValue(value, unit, decimals) {
  const v = toNumber(value);
  if (v === null) return null;
  const u = normalizeUnit(unit);
  const dp =
    Number.isInteger(decimals) && decimals >= 0 && decimals <= 6
      ? decimals
      : UNIT_DEFAULT_DECIMALS[u];
  const neg = v < 0;
  const a = Math.abs(v);
  const sign = neg ? "-" : "";
  switch (u) {
    case "usd":
    case "usd_per_unit":
      return `${sign}$${group(a, dp)}`;
    case "fraction":
      return `${sign}${group(a * 100, dp)}%`;
    default:
      return `${sign}${group(a, dp)}`;
  }
}

function compact(x) {
  if (x >= 1e6) return `${(x / 1e6).toFixed(x >= 1e7 ? 0 : 1)}M`;
  if (x >= 1e3) return `${(x / 1e3).toFixed(x >= 1e4 ? 0 : 1)}k`;
  if (Number.isInteger(x)) return String(x);
  return x.toFixed(x < 10 ? 2 : 1);
}

/** Compact tick label for the chart's y axis, by unit. */
export function fmtAxis(value, unit) {
  const v = toNumber(value);
  if (v === null) return "";
  const u = normalizeUnit(unit);
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (u === "usd" || u === "usd_per_unit") return `${sign}$${compact(a)}`;
  if (u === "fraction") {
    const p = a * 100;
    const whole = Math.abs(p - Math.round(p)) < 1e-9;
    return `${sign}${p.toFixed(p < 10 && !whole ? 1 : 0)}%`;
  }
  return `${sign}${compact(a)}`;
}

/* ------------------------------------------------------------------ */
/* Targets                                                             */
/* ------------------------------------------------------------------ */

// "0.22", "22%", "7-8%", "7 to 8%", "target 5". Nothing with words other
// than a leading "target", so a source path or an assumption note never
// parses as a level.
const TARGET_RE =
  /^\s*(?:target\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)\s*(%?)\s*(?:(?:-|to)\s*(-?\d+(?:\.\d+)?)\s*(%?))?\s*$/i;

/** A hand-typed target has a few decimals; a spilled formula has sixteen. */
const MAX_TARGET_DECIMALS = 4;

/** A target this far outside the metric's own range is not a target for it. */
const TARGET_RANGE_FACTOR = 4;

function decimalsOf(text) {
  const m = /\.(\d+)/.exec(text);
  return m ? m[1].length : 0;
}

/**
 * The numeric target the workbook keeps in a row's note column, as a band
 * on the item's unit scale, or null when the note is not a target.
 *
 *   "0.22"  on a fraction item -> 22% line
 *   "7-8%"  on a fraction item -> 7% to 8% band
 *   "5"     on a number item   -> 5 line
 *   "38"    on an MPG item whose values run 3 to 6 -> ignored (outside the
 *           metric's range by more than TARGET_RANGE_FACTOR)
 *   "3.0354545454545447" -> ignored (a spilled computation, not a target)
 */
export function parseTarget(note, item) {
  if (typeof note !== "string") return null;
  const m = TARGET_RE.exec(note);
  if (!m) return null;
  if (decimalsOf(m[1]) > MAX_TARGET_DECIMALS || (m[3] && decimalsOf(m[3]) > MAX_TARGET_DECIMALS)) {
    return null;
  }
  const unit = normalizeUnit(item ? item.unit : null);
  const anyPct = m[2] === "%" || m[4] === "%";
  let low = Number(m[1]);
  let high = m[3] !== undefined ? Number(m[3]) : low;
  if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
  if (unit === "fraction") {
    // Percent points ("22%", "7-8%", or a bare number above 1) scale down to
    // the fraction the row stores; "0.22" is already on that scale.
    if (anyPct || low > 1 || high > 1) {
      low /= 100;
      high /= 100;
    }
  } else if (anyPct) {
    // A percent note on a non-fraction item is a comment, not a level.
    return null;
  }
  if (low > high) [low, high] = [high, low];

  const vals = item && item.values ? Object.values(item.values).map((v) => Math.abs(v)) : [];
  if (vals.length) {
    const max = Math.max(...vals);
    if (max > 0 && (low > max * TARGET_RANGE_FACTOR || high < max / TARGET_RANGE_FACTOR)) {
      return null;
    }
  }

  // 0.07 * 100 is 7.000000000000001, so "whole percent" needs a tolerance.
  const wholePct = (x) => Math.abs(x * 100 - Math.round(x * 100)) < 1e-9;
  const dp = unit === "fraction" ? (wholePct(low) && wholePct(high) ? 0 : 1) : null;
  const fmt = (x) => fmtValue(x, unit, dp === null ? (item ? item.decimals : null) : dp);
  const label = low === high ? `target ${fmt(low)}` : `target ${fmt(low)} to ${fmt(high)}`;
  return { low, high, label };
}

/* ------------------------------------------------------------------ */
/* Chart scale                                                         */
/* ------------------------------------------------------------------ */

/** 1 / 2 / 2.5 / 5 step at the magnitude of `raw`. */
export function niceStep(raw) {
  const r = Math.abs(Number(raw));
  if (!Number.isFinite(r) || r === 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(r)));
  const q = r / p;
  const mult = q <= 1 ? 1 : q <= 2 ? 2 : q <= 2.5 ? 2.5 : q <= 5 ? 5 : 10;
  return mult * p;
}

/** Padding either side of a windowed [min, max] axis, as a share of the span. */
const WINDOW_PAD = 0.3;

/** A ratio series whose min is above this share of its max never nears zero. */
const WINDOW_RATIO_FLOOR = 0.6;

function round10(x) {
  return Number(x.toFixed(10));
}

/**
 * Axis bounds, ticks and the area baseline for a plotted series.
 *
 * Zero is on the axis whenever the series or its target touches or crosses
 * zero; an all-negative series has hi clamped to 0. The area baseline is
 * max(lo, 0) clamped inside the plot, so a positive series fills down to
 * zero, a negative one fills up to zero, and a windowed one fills from the
 * plot bottom.
 *
 * A series that never approaches zero (min above WINDOW_RATIO_FLOOR x max
 * on a fraction or ratio item, or any item carrying a target) gets a padded
 * [min, max] window that includes the target, so distance to target is
 * readable; `zeroBased` is false and the page says the axis does not start
 * at zero. A 21% to 24.5% series with a 22% target spans 18% to 26%.
 *
 * @param {(number|null)[]} values  the plotted points
 * @param {{ target?: {low:number, high:number}|null, ratio?: boolean }} [opts]
 */
export function chartScale(values, opts = {}) {
  const vals = (values ?? []).map(toNumber).filter((v) => v !== null);
  if (vals.length === 0) return null;
  const target =
    opts.target &&
    Number.isFinite(toNumber(opts.target.low)) &&
    Number.isFinite(toNumber(opts.target.high))
      ? { low: Number(opts.target.low), high: Number(opts.target.high) }
      : null;
  const all = target ? [...vals, target.low, target.high] : vals;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const touchesZero = min <= 0 && max >= 0;
  const windowed =
    !touchesZero &&
    min > 0 &&
    (target !== null || (opts.ratio === true && min > WINDOW_RATIO_FLOOR * max));
  let lo;
  let hi;
  if (windowed) {
    const span = max - min;
    const pad = span > 0 ? span * WINDOW_PAD : Math.abs(max) * 0.1 || 1;
    lo = min - pad;
    hi = max + pad;
  } else {
    lo = Math.min(0, min);
    hi = Math.max(0, max);
  }
  if (hi === lo) hi = lo + (Math.abs(lo) || 1);
  const step = niceStep((hi - lo) / 4);
  lo = round10(Math.floor(lo / step) * step);
  hi = round10(Math.ceil(hi / step) * step);
  // Padding must never invent a negative floor under a positive series.
  if (windowed && lo < 0) lo = 0;
  if (hi <= lo) hi = round10(lo + step);
  const n = Math.round((hi - lo) / step);
  const ticks = [];
  for (let k = 0; k <= n; k += 1) ticks.push(round10(lo + k * step));
  const zeroBased = lo <= 0 && hi >= 0;
  const baseline = Math.min(Math.max(lo, 0), hi);
  return { lo, hi, step, ticks, zeroBased, baseline };
}

/**
 * The plotted line broken at every month with no numeric value. Blank months
 * are gaps; error months (cell_text present) are returned separately so the
 * chart can mark them on the axis with the raw text. `first` / `last` are
 * the plotted range for the chart's accessible label.
 */
export function seriesSegments(item, months) {
  const segments = [];
  const points = [];
  const errors = [];
  let current = [];
  (Array.isArray(months) ? months : []).forEach((m, i) => {
    const v = itemValue(item, m);
    if (v !== null) {
      const p = { i, m, v };
      current.push(p);
      points.push(p);
      return;
    }
    if (current.length) {
      segments.push(current);
      current = [];
    }
    const text = itemCellText(item, m);
    if (text !== null) errors.push({ i, m, text });
  });
  if (current.length) segments.push(current);
  return {
    segments,
    points,
    errors,
    first: points.length ? points[0].m : null,
    last: points.length ? points[points.length - 1].m : null,
  };
}

/**
 * How many month labels to skip so at most `maxLabels` land on the axis.
 * 19 months at the default reads every other month, as the replica did.
 */
export function labelStride(monthCount, maxLabels = 14) {
  const n = Math.max(0, Math.trunc(Number(monthCount) || 0));
  const cap = Math.max(1, Math.trunc(Number(maxLabels) || 14));
  return n <= cap ? 1 : Math.ceil(n / cap);
}

/* ------------------------------------------------------------------ */
/* CSV                                                                 */
/* ------------------------------------------------------------------ */

/**
 * One quoted CSV cell. A text cell that starts with =, +, - or @ gets a
 * leading apostrophe so a spreadsheet opens it as text, never as a formula
 * (values are numbers and pass through untouched; this only bites notes and
 * labels).
 */
export function csvCell(x) {
  if (x === null || x === undefined) return '""';
  let s = String(x);
  if (typeof x === "string" && /^[=+\-@]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

/**
 * One block of one sheet as CSV, in the column order
 *   Sheet, Block, Metric, ...months, TTM <range>, TTM months present, Note,
 *   Unit, Provenance, Source
 * Sheet is the raw workbook sheet key. Values are raw numbers (not display
 * strings); a blank cell and an error cell both export empty; TTM is the
 * sum or the word "ratio", with the count of months present beside it.
 * Same row set the table shows.
 */
export function blockCsv(sheet, block, months) {
  const items = (sheet.blocks ?? []).find((b) => b.block === block)?.items ?? [];
  const window = (months ?? []).slice(-TTM_MONTHS);
  const ttmHeader = window.length
    ? `TTM ${monthLabel(window[0])} to ${monthLabel(window[window.length - 1])}`
    : "TTM";
  const head = [
    "Sheet",
    "Block",
    "Metric",
    ...months.map(monthLabel),
    ttmHeader,
    "TTM months present",
    "Note",
    "Unit",
    "Provenance",
    "Source",
  ];
  const lines = [head.map(csvCell).join(",")];
  for (const it of items) {
    const t = ttm(it, months);
    const cells = months.map((m) => itemValue(it, m) ?? "");
    lines.push(
      [
        sheet.sheet,
        block,
        it.lineItem,
        ...cells,
        t === null ? "ratio" : (t.value ?? ""),
        t === null ? "" : t.n,
        it.note ?? "",
        it.unit,
        provenanceTag(it.provenance).tag,
        SOURCE_LABELS[it.source === "ours" ? "ours" : "workbook"],
      ]
        .map(csvCell)
        .join(","),
    );
  }
  return lines.join("\n");
}

/** A file-system-safe name for the block export. */
export function csvFileName(sheetLabelText, block) {
  const clean = (s) =>
    String(s)
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  return `${clean(sheetLabelText)}_${clean(block)}.csv`;
}
